// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse } from "react-bootstrap";
import { NavigateFunction } from "react-router-dom";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { Command, UserRole } from "graphql/api";
import { CloudSettings, DeviceType } from "dbtypes";
import { DataState, MainPanel } from "types";
import { isSet } from "utils";
import { IAction, ActionType, raiseAction } from "actions";
import { IActionTypeChangeSite, IActionCommand, IActionTypeConfirmPopup } from "actions";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { IPendingCommand } from "states/schedulerstates";
import { isStateDirty, makeCommandSeqNr } from "guiutils";

import SiteSettings from "view/comp/SiteSettings";
import AlarmSettings from "view/comp/AlarmSettings";
import Reports from "view/page/Reports";
import ModalTrendMonitorSettings from "view/modal/ModalTrendMonitorSettings";
import Importer from "view/comp/Importer";
import SaveFeedback from "view/comp/SaveFeedback";

import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

interface ISiteAdminProps {
  site: Site | undefined;
  cloudSettings: CloudSettings | undefined;
  isAdminUser: boolean;
  showSshTunnel: boolean;
  canRebootController: boolean;
  dataState: DataState;
  pendingCommand: IPendingCommand | undefined;
  canModifyReports: boolean;
  canModifyTrendMonitors: boolean;
  canModifyAlarms: boolean;
  canImportTrendData: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified site", siteId, state.site.siteList);
  }
  const isAdminUser = [UserRole.admin, UserRole.root].includes(curUser?.role ?? UserRole.viewer);

  const props: ISiteAdminProps = {
    site,
    cloudSettings: site?.cloudSettings,
    isAdminUser,
    showSshTunnel:
      (curUser?.accessRights.openSshTunnel ?? false) &&
      isSet(state.scheduler.allData.lastData) &&
      site?.deviceType === DeviceType.EMBYT_STRATO,
    canRebootController:
      Boolean(curUser?.accessRights.controllerAdmin) &&
      isSet(state.scheduler.allData.lastData) &&
      site?.deviceType === DeviceType.EMBYT_STRATO,
    dataState: state.scheduler.saveState,
    pendingCommand: state.scheduler.pendingCommand,
    canModifyReports: curUser?.accessRights.createReports ?? false,
    canModifyTrendMonitors: curUser?.accessRights.crudTrendMonitors ?? false,
    canModifyAlarms: curUser?.accessRights.modifyAlarms ?? false,
    canImportTrendData: curUser?.accessRights.importTrendData ?? false,
  };
  return props;
};

type ISiteAdminState = {
  isAlarmExpanded: boolean;
  isTrendMonitorExpanded: boolean;
  isTrendImporterExpanded: boolean;
  cloudSettings: CloudSettings;
};

interface ISiteAdminDispatch {
  onSshTunnelClick: (title: string, message: string) => void;
  onRebootClick: (title: string, message: string) => void;
  onEditTrendMonitor: (index?: number) => void;
  onCancelClick: (navigate: NavigateFunction) => void;
  onOkClick: (id: number, cloudSettings: CloudSettings) => void;
  onChanges: (dataState: DataState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteAdminDispatch = {
    onSshTunnelClick: (title: string, message: string) => {
      const confirmPayload: IActionCommand = {
        commandSeqNr: makeCommandSeqNr(),
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_OPEN_SSH_TUNNEL_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onRebootClick: (title: string, message: string) => {
      const confirmPayload: IActionCommand = {
        commandSeqNr: makeCommandSeqNr(),
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_REBOOT_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onEditTrendMonitor: (index?: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_TRENDMONITOR, index));
    },
    onCancelClick: (navigate: NavigateFunction) => {
      navigate(`../${MainPanel.SITES}`, { state: { resetUnsavedChanges: true } });
    },
    onOkClick: (id: number, cloudSettings: CloudSettings) => {
      const payload: IActionTypeChangeSite = {
        id,
        cloudSettings,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_REQ, payload));
    },
    onChanges: (dataState: DataState) => {
      if (!isStateDirty(dataState)) {
        dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  confirmSshTunnelTitle: {
    id: "Open SSH Tunnel",
  },
  confirmSshTunnelBody: {
    id: "This will open an SSH tunnel to the embyt server, which stays active for one hour. Connect via ssh -p 43022 embyt@enl-tun.embyt.com. Are you sure?",
  },
  confirmRebootTitle: {
    id: "Reboot Controller",
  },
  confirmRebootBody: {
    id: "This will reboot the enline controller, which will stop energy regulation and may last several minutes. Are you sure?",
  },
  selectDeletionCaption: {
    id: "Select deletion strategy",
  },
});

class SiteAdminComponent extends React.Component<
  ISiteAdminProps & ISiteAdminDispatch & WrappedComponentProps & RoutedProps,
  ISiteAdminState
> {
  state: ISiteAdminState = {
    cloudSettings: {},
    isTrendMonitorExpanded: false,
    isAlarmExpanded: false,
    isTrendImporterExpanded: false,
  };

  componentDidMount() {
    if (this.props.cloudSettings) {
      this.setState({
        cloudSettings: this.props.cloudSettings,
        isTrendMonitorExpanded: false,
        isAlarmExpanded: false,
      });
    }
  }

  public render() {
    return (
      <div id="siteadmin">
        <div className="savebox">
          {this.renderSaveSettings()}
          <SaveFeedback className="mb-2 mt-2" dataState={this.props.dataState} />
        </div>
        <h1>
          <FormattedMessage id="Site Administration" />
        </h1>
        {this.renderButtons()}
        {this.props.isAdminUser && (
          <SiteSettings
            cloudSettings={this.state.cloudSettings}
            onSetCloudSettings={(cloudSettings) => {
              this.setState({ cloudSettings });
              this.props.onChanges(this.props.dataState);
            }}
          />
        )}
        {this.props.canModifyAlarms && (
          <AlarmSettings
            settings={this.state.cloudSettings.alarm}
            onChange={(newSettings) => {
              this.setState({
                cloudSettings: {
                  ...this.state.cloudSettings,
                  alarm: newSettings,
                },
              });
              this.props.onChanges(this.props.dataState);
            }}
          />
        )}
        <Reports isReadOnly={!this.props.canModifyReports} isAdminPage={true} />
        {this.props.canModifyTrendMonitors &&
          this.props.site?.deviceType !== DeviceType.VIRTUAL &&
          this.renderTrendMonitors()}
        {this.props.canImportTrendData && this.renderImports()}
      </div>
    );
  }

  private renderSaveSettings() {
    return (
      <div className="mb-2 mt-2">
        <Button
          variant="secondary"
          id="cancelSiteAdmin"
          type="submit"
          onClick={() => this.props.onCancelClick(this.props.navigate)}
        >
          {isStateDirty(this.props.dataState) ? (
            <FormattedMessage id="Cancel and Back" />
          ) : (
            <FormattedMessage id="Back to Site Selection" />
          )}
        </Button>
        {isSet(this.props.site) && isStateDirty(this.props.dataState) && (
          <Button
            variant="primary"
            id="saveSiteAdmin"
            type="submit"
            className="ms-2"
            onClick={(_ev) => {
              // use report settings from props
              const cloudSettings = {
                ...this.state.cloudSettings,
                reports: this.props.cloudSettings?.reports,
              };
              this.props.onOkClick(this.props.site!.id, cloudSettings);
            }}
          >
            <FormattedMessage id="Save Changes" />
          </Button>
        )}
      </div>
    );
  }

  private renderButtons() {
    return (
      <div className="buttonbox mb-3">
        {this.props.canRebootController && (
          <Button
            id="reboot"
            variant="danger"
            onClick={(e) =>
              this.props.onRebootClick(
                this.props.intl.formatMessage(messages.confirmRebootTitle),
                this.props.intl.formatMessage(messages.confirmRebootBody),
              )
            }
          >
            <FormattedMessage id="Reboot Controller" />
          </Button>
        )}
        {this.props.pendingCommand?.command === Command.rebootController && (
          <div className="alert alert-warning">
            <FormattedMessage id="Execution Pending" />
          </div>
        )}
        {this.props.showSshTunnel && (
          <Button
            id="sshTunnel"
            variant="secondary"
            onClick={(e) =>
              this.props.onSshTunnelClick(
                this.props.intl.formatMessage(messages.confirmSshTunnelTitle),
                this.props.intl.formatMessage(messages.confirmSshTunnelBody),
              )
            }
          >
            <FormattedMessage id="Open SSH Tunnel" />
          </Button>
        )}
        {this.props.pendingCommand?.command === Command.openSshTunnel && (
          <div className="alert alert-warning">
            <FormattedMessage id="Execution Pending" />
          </div>
        )}
      </div>
    );
  }

  private renderTrendMonitors() {
    const trendMonitorItems = (this.props.cloudSettings?.trendMonitor ?? []).map(
      (curItem, curIndex) => {
        const linkClasses = ["link"];
        if (!curItem.isActive) linkClasses.push("deactivated");
        return (
          <tr key={curIndex} className="mt-1 mb-1">
            <td>
              <span
                onClick={() => this.props.onEditTrendMonitor(curIndex)}
                className={linkClasses.join(" ")}
              >
                {curItem.title}
              </span>
            </td>
          </tr>
        );
      },
    );

    return (
      <div id="trendmonitorlist">
        <h1
          className="link"
          onClick={() => {
            this.setState({ isTrendMonitorExpanded: !this.state.isTrendMonitorExpanded });
          }}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.state.isTrendMonitorExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Trend Monitors" />
        </h1>
        <Collapse in={this.state.isTrendMonitorExpanded}>
          <div id="trendmonitorentries">
            <table className="fullwidthwhite">
              <tbody>
                {trendMonitorItems}
                <tr>
                  <td>
                    <Button
                      id="newreport"
                      variant="secondary"
                      className="mt-1 mb-1"
                      onClick={(e) => this.props.onEditTrendMonitor()}
                    >
                      <FormattedMessage id="Create New" />
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Collapse>
        <ModalTrendMonitorSettings />
      </div>
    );
  }

  private renderImports() {
    return (
      <div id="trendimporter">
        <h1
          className="link"
          onClick={() => {
            this.setState({ isTrendImporterExpanded: !this.state.isTrendImporterExpanded });
          }}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.state.isTrendImporterExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Import Trend Data" />
        </h1>
        <Collapse in={this.state.isTrendImporterExpanded}>
          <div className="fullwidthwhite">
            <Importer />
          </div>
        </Collapse>
      </div>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteAdminComponent)),
);
